import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from './Toast';
import { CartContext } from '../context/CartContext';
import { useWishlistContext } from '../context/WishlistContext';
import AuthContext from '../context/AuthContext';
import { API_URL, productPath } from '../config';

const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return `${API_URL}/${imagePath.replace(/\\/g, '/')}`;
};

export default function TVProductCard({ product }) {
    const navigate = useNavigate();
    const { addToCart } = useContext(CartContext);
    const { isAuthenticated } = useContext(AuthContext);
    const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlistContext();
    const [adding, setAdding] = useState(false);
    const [wishlistLoading, setWishlistLoading] = useState(false);

    const defaultVariantId = product.defaultVariantId || product.variantId;
    const isWishlisted = defaultVariantId ? isInWishlist(defaultVariantId) : false;
    const productLink = productPath(
        product.variantSlug
        || product.variant?.slug
        || product.variants?.[0]?.slug
        || product.slug
        || product._id
        || product.id
    );

    const handleAddToCart = async (e) => {
        e.stopPropagation();
        if (defaultVariantId) {
            try {
                setAdding(true);
                await addToCart(defaultVariantId, 1);
                toast.success(`${product.name} added to cart!`);
            } catch (err) {
                const msg = err.response?.data?.message || 'Failed to add to cart';
                toast.error(msg);
            } finally {
                setAdding(false);
            }
        } else {
            navigate(productLink);
        }
    };

    const handleCardClick = () => {
        navigate(productLink);
    };

    const handleWishlistClick = async (e) => {
        e.stopPropagation();
        if (!isAuthenticated) {
            toast.info('Please login to add items to your wishlist');
            navigate('/login');
            return;
        }
        if (!defaultVariantId) {
            toast.info('Please open the product to select a variant first');
            return;
        }
        setWishlistLoading(true);
        try {
            if (isWishlisted) {
                await removeFromWishlist(defaultVariantId);
                toast.success('Removed from wishlist');
            } else {
                await addToWishlist(defaultVariantId);
                toast.success('Added to wishlist!');
            }
        } catch (err) {
            const msg = err?.response?.data?.message || 'Failed to update wishlist';
            toast.error(msg);
        } finally {
            setWishlistLoading(false);
        }
    };

    return (
        <div
            onClick={handleCardClick}
            className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-shadow duration-300 group cursor-pointer flex flex-col h-full"
        >
            {/* Image Container */}
            <div className="relative w-full h-64 overflow-hidden bg-muted">
                <img
                    src={getImageUrl(product.image)}
                    alt={product.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                />

                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                    {product.discount > 0 && (
                        <span className="px-2 py-1 bg-primary text-white text-xs font-bold rounded whitespace-nowrap">
                            {product.discount}% OFF
                        </span>
                    )}
                </div>

                {/* Wishlist Button */}
                <button
                    onClick={handleWishlistClick}
                    disabled={wishlistLoading}
                    className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-card rounded-full shadow-md hover:bg-muted transition-colors disabled:opacity-60"
                >
                    {wishlistLoading ? (
                        <i className="ri-loader-4-line animate-spin text-primary text-lg"></i>
                    ) : (
                        <i className={`${isWishlisted ? 'ri-heart-fill text-primary' : 'ri-heart-line text-muted-foreground'} text-lg`}></i>
                    )}
                </button>
            </div>

            {/* Content */}
            <div className="p-4 flex flex-col flex-1">
                {/* Brand */}
                <p className="text-xs text-muted-foreground mb-1">{product.brand}</p>

                {/* Product Name */}
                <h3 className="text-sm font-semibold text-foreground mb-2 line-clamp-2 min-h-[40px]">
                    {product.name}
                </h3>

                {/* Spacer to push bottom content down */}
                <div className="flex-1"></div>

                {/* Price */}
                <div className="mb-3">
                    <div className="flex items-baseline gap-2">
                        <span className="text-xl font-bold text-foreground">
                            KSh {product.price.toLocaleString()}
                        </span>
                        <span className="text-sm text-muted-foreground line-through">
                            KSh {product.originalPrice.toLocaleString()}
                        </span>
                    </div>
                    <p className="text-xs text-success mt-1 h-5">
                        <i className="ri-bank-card-line"></i> {product.emi}
                    </p>
                </div>

                {/* KCB Offer Badge or Spacer */}
                <div className="mb-3 h-6">
                    {product.kcbOffer ? (
                        <span className="inline-block px-2 py-1 bg-[#E8F5E9] text-success text-xs font-bold rounded whitespace-nowrap">
                            KCB Offer
                        </span>
                    ) : null}
                </div>

                {/* Action Button */}
                <button
                    onClick={handleAddToCart}
                    disabled={adding}
                    className="w-full py-2.5 bg-primary text-white rounded-lg font-medium text-sm hover:brightness-110 hover:shadow-lg transition-all duration-300 whitespace-nowrap mt-auto opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 disabled:opacity-60"
                >
                    {adding ? 'Adding...' : <><i className="ri-shopping-cart-line mr-1"></i> Add to Cart</>}
                </button>
            </div>
        </div>
    );
}
